import { useCallback, useMemo } from "react";
import { useRealStudents } from "@/hooks/useRealStudents";
import { useAllAttendanceRecords } from "@/hooks/useAllAttendanceRecords";
import { useLateAttendance } from "@/hooks/useLateAttendance";
import { useRealSchoolSettings } from "@/hooks/useRealSchoolSettings";

import { useAttendanceState } from "@/hooks/useAttendanceState";
import { useReportsData } from "@/hooks/useReportsData";
import { useReportsFilters } from "@/hooks/useReportsFilters";
import { ReportsDataValidation } from "@/components/reports/core/ReportsDataValidation";
import { ReportsHeader } from "@/components/reports/core/ReportsHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AttendanceReportTab } from "@/components/reports/tabs/AttendanceReportTab";
import { LateAttendanceReportTab } from "@/components/reports/tabs/LateAttendanceReportTab";
import { StudentReportTab } from "@/components/reports/tabs/StudentReportTab";
import { LateReportTab } from "@/components/reports/tabs/LateReportTab";
import { dateSplitter } from "@/utils/formatting/deteSplitter";

const ReportsBeta = () => {
  const { students } = useRealStudents();

  const { settings } = useRealSchoolSettings();
  const { enabledStages, hasMultipleStages } = useAttendanceState();

  // console.log("Students:", students);
  // console.log("Attendance records:", attendanceRecords);

  // Ensure enabledStages is an array and filter out invalid stages
  const validEnabledStages = useMemo(() => {
    const validStages = Array.isArray(enabledStages)
      ? enabledStages.filter((stage) => stage && stage.id && stage.name)
      : [];

    return validStages;
  }, [enabledStages]);

  // Initialize filters
  const {
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    selectedGrade,
    selectedSection,
    setSelectedSection,
    selectedStage,
    sortType,
    setSortType,
    handleStageChange,
    handleGradeChange,
    getSelectedStageDisplayName,
    getPeriodDateRange,
  } = useReportsFilters(validEnabledStages, []);
  const { attendanceRecords } = useAllAttendanceRecords(
    false,
    dateSplitter(startDate),
    dateSplitter(endDate),
    selectedGrade,
    selectedSection
  );

  // Get period date range for filtering
const periodDateRange = useMemo(() => getPeriodDateRange(), [getPeriodDateRange]);

  // Create students with attendance data
  const studentsWithAttendance = useMemo(() => {
    return students.map((student) => {
      const studentAttendanceRecords = attendanceRecords.filter(
        (record) => record.student_id === student.id
      );
      const totalDays = studentAttendanceRecords.length;
      const presentDays = studentAttendanceRecords.filter(
        (record) => record.status === "present"
      ).length;
      const lateDays = studentAttendanceRecords.filter(
        (record) => record.status === "late"
      ).length;
      const absentDays = studentAttendanceRecords.filter(
        (record) =>
          record.status === "absent" || record.status === "absent_with_reason"
      ).length;

      const absentWithReasonDays = studentAttendanceRecords.filter(
        (record) => record.status === "absent_with_reason"
      ).length;
      const attendancePercentage =
        totalDays > 0
          ? Math.round(((presentDays + lateDays) / totalDays) * 100)
          : 0;

      return {
        id: student.id,
        name: student.name,
        grade: student.grade,
        section: student.section,
        studentId: student.student_id,
        absenceDays: absentDays,
        absentWithReasonDays,
        lateDays,
        attendancePercentage,
        isPresent: undefined,
        status: undefined,
      };
    });
  }, [students, attendanceRecords]);

  // Get filtered grades and sections based on actual student data
  const { filteredAvailableGrades, availableSectionsForGrade } = useMemo(() => {


    // Calculate available grades based on selected stage
    let availableGrades = [];
    if (selectedStage && selectedStage !== "all") {
      const stage = validEnabledStages.find((s) => s?.id === selectedStage);
      if (stage && stage.grades) {
        availableGrades = stage.grades
          .map((g) => g?.name || "")
          .filter((name) => name);
      }
    } else {
      // Get all available grades from students
      availableGrades = [
        ...new Set(
          studentsWithAttendance.map((s) => s?.grade).filter((grade) => grade)
        ),
      ];
      // console.log("Grades from student data:", availableGrades);
    }

    // Calculate available sections for selected grade
    let availableSections = [];
    if (selectedGrade && selectedGrade !== "all") {
      if (selectedStage && selectedStage !== "all") {
        const stage = validEnabledStages.find((s) => s?.id === selectedStage);
        if (stage && stage.grades) {
          const gradeObj = stage.grades.find((g) => g?.name === selectedGrade);
          if (gradeObj && gradeObj.sections) {
            availableSections = gradeObj.sections.filter((section) => section);
          }
        }
      }

      // If no sections in stage settings, use student sections
      if (availableSections.length === 0) {
        availableSections = [
          ...new Set(
            studentsWithAttendance
              .filter((s) => s?.grade === selectedGrade)
              .map((s) => s?.section)
              .filter((section) => section)
          ),
        ];
      }

      if (availableSections.length === 0) {
        availableSections = ["أ"];
      }
    }

    return {
      filteredAvailableGrades: availableGrades,
      availableSectionsForGrade: availableSections,
    };
  }, [
    validEnabledStages,
    studentsWithAttendance,
    selectedStage,
    selectedGrade,
  ]);

  // Filter attendance records based on period date range
  const filteredAttendanceRecords = useMemo(() => {
    const filtered = attendanceRecords.filter((record) => {
      const student = students.find((s) => s.id === record.student_id);

      if (!student) {
        return false;
      }

      // تطبيق نفس الفلاتر المستخدمة في filteredStudents
      if (selectedStage) {
        const stage = validEnabledStages.find((s) => s.id === selectedStage);
        if (stage && stage.grades) {
          const stageGrades = stage.grades.map((g) => g.name);
          if (!stageGrades.includes(student.grade)) {
            // console.log("Student grade not in selected stage:", student.grade);
            return false;
          }
        }
      }


      return true;
    });

    return filtered;
  }, [attendanceRecords, selectedStage, students, validEnabledStages]);

  // Get processed data with lateRecords and period filtering

  const { lateRecords } = useLateAttendance(
    dateSplitter(startDate),
    dateSplitter(endDate),
    selectedGrade,
    selectedSection
  );
  // console.log("lateRecords", lateRecords);
  const {
    filteredStudents,
    studentsWithAlerts,
    studentsWithLateAlerts,
    mostAbsentStudents,
    mostLateStudents,
    bestAttendanceStudents,
    weeklyData,
    gradeData,
    overallStats,
  } = useReportsData(
    studentsWithAttendance,
    selectedStage,
    selectedGrade,
    selectedSection,
    validEnabledStages,
    filteredAttendanceRecords, // Use filtered attendance records
    lateRecords,
    periodDateRange,
    settings?.alert_rules // مرر إعدادات التنبيهات
  );

  // console.log("mostLateStudents", mostLateStudents);
  // console.log("lateRecords", lateRecords);
  // console.log("filteredStudents", filteredStudents);
  // تصفية سجلات التأخر حسب الفلاتر المحددة
  const filteredLateRecords = useMemo(() => {
    const filtered = lateRecords.filter((record) => {
      const student = students.find((s) => s.id === record.studentId);
      // console.log(
      //   "Checking late record for student:",
      //   record.studentId,
      //   "found student:",
      //   student?.name
      // );

      if (!student) {
        // console.log("Student not found for late record:", record.studentId);
        return false;
      }

      // تطبيق نفس الفلاتر المستخدمة في filteredStudents
      if (selectedStage) {
        const stage = validEnabledStages.find((s) => s.id === selectedStage);
        if (stage && stage.grades) {
          const stageGrades = stage.grades.map((g) => g.name);
          if (!stageGrades.includes(student.grade)) {
            // console.log("Student grade not in selected stage:", student.grade);
            return false;
          }
        }
      }

      // if (selectedGrade && student.grade !== selectedGrade)  {
      //   // console.log(
      //   //   "Student grade does not match selected grade:",
      //   //   student.grade,
      //   //   "vs",
      //   //   selectedGrade
      //   // );
      //   return false;
      // }

      // if (selectedSection && student.section !== selectedSection) {
      //   // console.log(
      //   //   "Student section does not match selected section:",
      //   //   student.section,
      //   //   "vs",
      //   //   selectedSection
      //   // );
      //   return false;
      // }

      // console.log("Late record passed all filters for student:", student.name);
      return true;
    });

    return filtered;
  }, [lateRecords, students, selectedStage, validEnabledStages]);

  const hasData = students.length > 0 || attendanceRecords.length > 0;

  // Safe function to get stage display name
const safeGetStageDisplayName = useCallback((stage: any) => {
  if (!stage) return "";
  return typeof stage === "string" ? stage : stage.name || "";
}, []);


  return (
    <div className="container mx-auto px-3 py-4" dir="rtl">
      <ReportsHeader hasData={hasData} />
      <ReportsDataValidation
        studentsCount={students.length}
        attendanceRecordsCount={attendanceRecords.length}
        studentsWithAlertsCount={studentsWithAlerts.length}
        studentsWithLateAlertsCount={studentsWithLateAlerts.length}
      />
      {!hasData && (
        <Card className="bg-white/95 backdrop-blur-sm border-blue-200/50 shadow-lg">
          <CardContent className="p-8 text-center">
            <div className="space-y-4">
              <div className="w-16 h-16 mx-auto bg-blue-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-800">
                لا توجد بيانات متاحة
              </h3>
              <p className="text-gray-600 max-w-md mx-auto">
                لعرض التقارير، تحتاج أولاً إلى إضافة طلاب وتسجيل بيانات الحضور.
                ابدأ بإضافة الطلاب من قسم "إدارة الطلاب" ثم سجل الحضور من قسم
                "تسجيل الحضور".
              </p>
            </div>
          </CardContent>
        </Card>
      )}
      {hasData && (
        <div className="space-y-6">
          <Tabs defaultValue="attendance" className="w-full" dir="rtl">
            <div className="w-full overflow-x-auto">
              <TabsList className="inline-flex h-auto w-auto min-w-full p-1 bg-muted rounded-lg">
                <TabsTrigger
                  value="attendance"
                  className="flex-1 min-w-0 text-xs px-2 py-2 whitespace-nowrap data-[state=active]:bg-background data-[state=active]:text-foreground"
                >
                  تقرير حضور فصل
                </TabsTrigger>
                <TabsTrigger
                  value="lateness"
                  className="flex-1 min-w-0 text-xs px-2 py-2 whitespace-nowrap data-[state=active]:bg-background data-[state=active]:text-foreground"
                >
                  تقرير تأخر فصل
                </TabsTrigger>
                <TabsTrigger
                  value="student"
                  className="flex-1 min-w-0 text-xs px-2 py-2 whitespace-nowrap data-[state=active]:bg-background data-[state=active]:text-foreground"
                >
                  تقرير حضور طالب
                </TabsTrigger>
                <TabsTrigger
                  value="late-student"
                  className="flex-1 min-w-0 text-xs px-2 py-2 whitespace-nowrap data-[state=active]:bg-background data-[state=active]:text-foreground"
                >
                  تقرير تأخر طالب
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="attendance" className="space-y-6">
              <AttendanceReportTab
                hasData={hasData}
                filteredStudents={filteredStudents}
                studentsWithAlerts={studentsWithAlerts}
                studentsWithLateAlerts={studentsWithLateAlerts}
                mostAbsentStudents={mostAbsentStudents}
                bestAttendanceStudents={bestAttendanceStudents}
                weeklyData={weeklyData}
                gradeData={gradeData}
                overallStats={overallStats}
                getSelectedStageDisplayName={getSelectedStageDisplayName}
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
                selectedStage={selectedStage}
                handleStageChange={handleStageChange}
                selectedGrade={selectedGrade}
                handleGradeChange={handleGradeChange}
                selectedSection={selectedSection}
                setSelectedSection={setSelectedSection}
                hasMultipleStages={hasMultipleStages}
                validEnabledStages={validEnabledStages}
                safeGetStageDisplayName={safeGetStageDisplayName}
                filteredAvailableGrades={filteredAvailableGrades}
                availableSectionsForGrade={availableSectionsForGrade}
                sortType={sortType}
                setSortType={setSortType}
                periodDateRange={periodDateRange}
              />
            </TabsContent>

            <TabsContent value="lateness" className="space-y-6">
              <LateAttendanceReportTab
                filteredStudents={filteredStudents}
                mostLateStudents={mostLateStudents}
                filteredLateRecords={filteredLateRecords}
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
                selectedStage={selectedStage}
                handleStageChange={handleStageChange}
                selectedGrade={selectedGrade}
                handleGradeChange={handleGradeChange}
                selectedSection={selectedSection}
                setSelectedSection={setSelectedSection}
                hasMultipleStages={hasMultipleStages}
                validEnabledStages={validEnabledStages}
                safeGetStageDisplayName={safeGetStageDisplayName}
                filteredAvailableGrades={filteredAvailableGrades}
                availableSectionsForGrade={availableSectionsForGrade}
                periodDateRange={periodDateRange}
              />
            </TabsContent>

            <TabsContent value="student" className="space-y-6">
              <StudentReportTab
                students={students}
                // attendanceRecords={attendanceRecords}
                // lateRecords={lateRecords}
                validEnabledStages={validEnabledStages}
                safeGetStageDisplayName={safeGetStageDisplayName}
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
                selectedStage={selectedStage}
                handleStageChange={handleStageChange}
                selectedGrade={selectedGrade}
                handleGradeChange={handleGradeChange}
                selectedSection={selectedSection}
                setSelectedSection={setSelectedSection}
                filteredAvailableGrades={filteredAvailableGrades}
                availableSectionsForGrade={availableSectionsForGrade}
              />
            </TabsContent>

            <TabsContent value="late-student" className="space-y-6">
              <LateReportTab
                students={students}
                lateRecords={lateRecords}
                validEnabledStages={validEnabledStages}
                safeGetStageDisplayName={safeGetStageDisplayName}
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
                selectedStage={selectedStage}
                handleStageChange={handleStageChange}
                selectedGrade={selectedGrade}
                handleGradeChange={handleGradeChange}
                selectedSection={selectedSection}
                setSelectedSection={setSelectedSection}
                filteredAvailableGrades={filteredAvailableGrades}
                availableSectionsForGrade={availableSectionsForGrade}
              />
            </TabsContent>
          </Tabs>
        </div>
      )}{" "}
    </div>
  );
};

export { ReportsBeta as default };
